/**
 * BCE (BizCloud Experts) Material-UI Theme Configuration
 * 
 * Applies BCE branding consistently across all MUI components
 */
import { createTheme } from '@mui/material/styles';

const bceTheme = createTheme({
  palette: {
    primary: {
      main: '#595cea',
      light: '#678ae7',
      dark: '#5851e1',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#4fc1f6',
      light: '#62c6fc',
      dark: '#3ba8dc',
      contrastText: '#ffffff',
    },
    text: {
      primary: '#1e1e1e',
      secondary: 'rgba(30, 30, 30, 0.7)',
      disabled: 'rgba(30, 30, 30, 0.5)',
    },
    background: {
      default: '#f9f9f9',
      paper: '#ffffff',
    },
    error: {
      main: '#f5b8c5',
      light: '#fdd8e0',
      dark: '#ed9aab',
    },
    warning: {
      main: '#f5dda8',
      light: '#fcedc8',
      dark: '#edc97a',
    },
    info: {
      main: '#b8d9f5',
      light: '#d8ebfa',
      dark: '#8fc5ed',
    },
    success: {
      main: '#b4e4ce',
      light: '#d4f4e7',
      dark: '#8fd4b8',
    },
  },
  typography: {
    fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    h1: {
      fontFamily: '"Poppins", system-ui, sans-serif',
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Poppins", system-ui, sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Poppins", system-ui, sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: '"Poppins", system-ui, sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontFamily: '"Poppins", system-ui, sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Poppins", system-ui, sans-serif',
      fontWeight: 600,
    },
    subtitle1: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 600,
    },
    subtitle2: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 600,
    },
    body1: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 400,
    },
    body2: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 400,
    },
    button: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 600,
      textTransform: 'none',
    },
    caption: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 400,
    },
    overline: {
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 10px 25px -5px rgba(89, 92, 234, 0.3)',
    '0 10px 25px -5px rgba(79, 193, 246, 0.3)',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 10px 25px -5px rgba(89, 92, 234, 0.3)',
    '0 10px 25px -5px rgba(79, 193, 246, 0.3)',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 10px 25px -5px rgba(89, 92, 234, 0.3)',
    '0 10px 25px -5px rgba(79, 193, 246, 0.3)',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: '"DM Sans", sans-serif',
          transition: 'all 0.3s ease',
        },
        contained: {
          boxShadow: '0 2px 8px rgba(89, 92, 234, 0.3)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(89, 92, 234, 0.4)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            fontFamily: '"DM Sans", sans-serif',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#ffffff',
            },
            '&.Mui-focused': {
              backgroundColor: '#ffffff',
              boxShadow: '0 0 0 3px rgba(89, 92, 234, 0.1)',
            },
          },
          '& .MuiInputLabel-root': {
            fontFamily: '"DM Sans", sans-serif',
            fontWeight: 500,
            color: '#1e1e1e',
          },
          '& .MuiFormHelperText-root': {
            fontFamily: '"DM Sans", sans-serif',
            fontWeight: 400,
            fontSize: '0.875rem',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          fontFamily: '"DM Sans", sans-serif',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: '"DM Sans", sans-serif',
          fontWeight: 500,
          color: '#1e1e1e',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: '"DM Sans", sans-serif',
          fontWeight: 400,
          fontSize: '0.875rem',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"DM Sans", sans-serif',
          fontWeight: 600,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontFamily: '"DM Sans", sans-serif',
          borderRadius: '12px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          fontFamily: '"DM Sans", sans-serif',
        },
        rounded: {
          borderRadius: '16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          fontFamily: '"DM Sans", sans-serif',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          fontFamily: '"DM Sans", sans-serif',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: '"DM Sans", sans-serif',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: '"DM Sans", sans-serif',
        },
        secondary: {
          fontFamily: '"DM Sans", sans-serif',
        },
      },
    },
  },
});

export default bceTheme;
